/**
 * TAXONOMÍA CONFIGURABLE POR ESCUELA
 * 
 * Permite a cada escuela definir su propia forma de clasificar prácticas.
 * Ejemplos:
 * - Escuela AMAR: presence, compasivos, admirativos, fraternales, eroticos
 * - Escuela Mindfulness: respiración, body-scan, meditación, walking, loving-kindness
 * - Escuela Yoga: asanas, pranayama, meditación, nidra, filosofía
 * - Escuela Psicoterapia: cognitivo, conductual, emocional, relacional, corporal
 */

export interface SchoolTaxonomyCategory {
  id: string;                    // ID único de la categoría
  name: string;                  // Nombre para mostrar (ej: "Presencia", "Amar-me")
  description?: string;          // Descripción de qué tipo de prácticas incluye
  color?: string;                // Color HEX para UI (#3B82F6)
  icon?: string;                 // Emoji o nombre de ícono
  keywords?: string[];           // Keywords para ayudar a la IA a clasificar
  order?: number;                // Orden de visualización
}

export interface SchoolTaxonomy {
  id: string;                    // ID único de la taxonomía
  schoolId: string;              // ID de la escuela
  name: string;                  // Nombre del sistema de clasificación
  description?: string;          // Descripción del sistema
  categories: SchoolTaxonomyCategory[];
  isActive: boolean;             // Permitir múltiples taxonomías, solo una activa
  createdAt: Date;
  updatedAt: Date;
}

/**
 * DTO para crear taxonomía
 */
export interface CreateSchoolTaxonomyDTO {
  schoolId: string;
  name: string;
  description?: string;
  categories: Omit<SchoolTaxonomyCategory, 'id'>[];
}

/**
 * DTO para actualizar taxonomía
 */
export interface UpdateSchoolTaxonomyDTO {
  name?: string;
  description?: string;
  categories?: SchoolTaxonomyCategory[];
  isActive?: boolean;
}

/**
 * TAXONOMÍAS PRE-DEFINIDAS
 * Para facilitar el setup inicial de escuelas nuevas
 */

export const PREDEFINED_TAXONOMIES: Record<string, Omit<SchoolTaxonomy, 'id' | 'schoolId' | 'createdAt' | 'updatedAt'>> = {
  'amar': {
    name: 'Metodología AMAR',
    description: 'Sistema de 5 pilares para el bienestar integral',
    isActive: true,
    categories: [
      {
        id: 'presence',
        name: 'Amar-me (Presencia)',
        description: 'Prácticas de autoconocimiento, presencia y mindfulness',
        color: '#8B5CF6',
        icon: '🧘',
        keywords: ['presencia', 'mindfulness', 'autoconocimiento', 'consciencia', 'atención'],
        order: 1
      },
      {
        id: 'compasivos',
        name: 'Manifestar (Compasión)',
        description: 'Prácticas de compasión, bondad y aceptación',
        color: '#EC4899',
        icon: '💝',
        keywords: ['compasión', 'bondad', 'aceptación', 'amor propio', 'ternura'],
        order: 2
      },
      {
        id: 'admirativos',
        name: 'Agradecer (Admiración)',
        description: 'Prácticas de gratitud, aprecio y reconocimiento',
        color: '#F59E0B',
        icon: '🙏',
        keywords: ['gratitud', 'aprecio', 'reconocimiento', 'admiración', 'celebración'],
        order: 3
      },
      {
        id: 'fraternales',
        name: 'Relacionar-me (Conexión)',
        description: 'Prácticas de conexión, comunicación y relaciones',
        color: '#10B981',
        icon: '🤝',
        keywords: ['conexión', 'relaciones', 'comunicación', 'empatía', 'comunidad'],
        order: 4
      },
      {
        id: 'eroticos',
        name: 'Eróticos (Pasión)',
        description: 'Prácticas de vitalidad, energía y pasión por vivir',
        color: '#EF4444',
        icon: '🔥',
        keywords: ['pasión', 'vitalidad', 'energía', 'creatividad', 'expresión'],
        order: 5
      }
    ]
  },
  
  'mindfulness': {
    name: 'Mindfulness Clásico',
    description: 'Prácticas tradicionales de atención plena',
    isActive: true,
    categories: [
      {
        id: 'breath',
        name: 'Respiración Consciente',
        description: 'Prácticas centradas en la respiración',
        color: '#3B82F6',
        icon: '🌬️',
        keywords: ['respiración', 'pranayama', 'breath', 'inhalar', 'exhalar'],
        order: 1
      },
      {
        id: 'body-scan',
        name: 'Escaneo Corporal',
        description: 'Prácticas de consciencia del cuerpo',
        color: '#8B5CF6',
        icon: '🧘‍♀️',
        keywords: ['cuerpo', 'sensaciones', 'escaneo', 'body scan', 'somático'],
        order: 2
      },
      {
        id: 'meditation',
        name: 'Meditación Sentada',
        description: 'Meditaciones formales sentadas',
        color: '#6366F1',
        icon: '🪷',
        keywords: ['meditación', 'sentada', 'formal', 'silencio', 'quietud'],
        order: 3
      },
      {
        id: 'walking',
        name: 'Meditación Caminando',
        description: 'Prácticas de atención en movimiento',
        color: '#10B981',
        icon: '🚶',
        keywords: ['caminar', 'movimiento', 'walking', 'paseo', 'consciencia'],
        order: 4
      },
      {
        id: 'loving-kindness',
        name: 'Bondad Amorosa',
        description: 'Metta y prácticas de compasión',
        color: '#EC4899',
        icon: '💖',
        keywords: ['metta', 'bondad', 'compasión', 'amor', 'benevolencia'],
        order: 5
      }
    ]
  },
  
  'psychotherapy': {
    name: 'Psicoterapia Integrativa',
    description: 'Ejercicios terapéuticos por enfoque',
    isActive: true,
    categories: [
      {
        id: 'cognitive',
        name: 'Cognitivo',
        description: 'Trabajo con pensamientos y creencias',
        color: '#3B82F6',
        icon: '🧠',
        keywords: ['pensamiento', 'cognición', 'creencias', 'reestructuración', 'mental'],
        order: 1
      },
      {
        id: 'behavioral',
        name: 'Conductual',
        description: 'Ejercicios de modificación de conducta',
        color: '#10B981',
        icon: '🎯',
        keywords: ['conducta', 'hábitos', 'acción', 'exposición', 'comportamiento'],
        order: 2
      },
      {
        id: 'emotional',
        name: 'Emocional',
        description: 'Regulación y procesamiento emocional',
        color: '#F59E0B',
        icon: '❤️',
        keywords: ['emoción', 'sentimientos', 'afecto', 'regulación', 'expresión'],
        order: 3
      },
      {
        id: 'relational',
        name: 'Relacional',
        description: 'Dinámicas interpersonales y vínculos',
        color: '#EC4899',
        icon: '👥',
        keywords: ['relación', 'vínculo', 'interpersonal', 'comunicación', 'apego'],
        order: 4
      },
      {
        id: 'somatic',
        name: 'Corporal/Somático',
        description: 'Trabajo corporal y sensorial',
        color: '#8B5CF6',
        icon: '🌊',
        keywords: ['cuerpo', 'somático', 'sensorial', 'trauma', 'tensión'],
        order: 5
      }
    ]
  }
};

